/* eslint-disable react/prop-types */

import './Profile.scss';

import * as React from 'react';
import { ChangeEvent, useEffect } from 'react';

import { translate } from '~l10n/client';
import cx from 'classnames';

import { FeatureUseEventAdapter } from '~analytics/adapters/FeatureUseEventAdapter';
import PhoneNumberInput from '~shared/components/PhoneNumberInput/PhoneNumberInput';
import { Spinner } from '~shared/components/Spinner';
import { UpdateEmailDialog } from '~teacher/components/Profile/ChangeEmail/UpdateEmailDialog';
import {
  ConfirmDeleteAccountDialog,
  UnableToDeleteAccountDialog,
} from '~teacher/components/Profile/DeleteAccount';
import { useIsOwnerOfTeamWithMembers } from '~teacher/hook/team/useIsOwnerOfTeamWithMembers';

import Input from '../../../../../shared/components/elements/Input';
import HelpTooltip from '../../../../../shared/components/tooltips/HelpTooltip';
import ProfileTabs from '../../../../teacher/profile/components/ProfileTabs';

interface Props {
  controller: {
    closeBannerClicked: () => void;
    firstNameChanged: (newName: string) => void;
    lastNameChanged: (newName: string) => void;
    changePasswordClicked: () => void;
    currentPasswordChanged: (newPassword: string) => void;
    newPasswordChanged: (newPassword: string) => void;
    newPassword2Changed: (newPassword: string) => void;
    phoneNumberChanged: (options: { value: any; hasError: any }) => void;
    showPasswordsChanged: () => void;
    saveClicked: () => void;
  };
  model: {
    banner?: { message: string; type: number };
    errors: {
      firstName: string;
      lastName: string;
      password: string;
      password2: string;
      currentPassword: string;
    };
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    showChangePassword: boolean;
    showPasswords: boolean;
    currentPassword: string;
    password: string;
    password2: string;
    deletingAccount: boolean;
  };
  selectedTab: string;
  passwordFieldsVisible: boolean;
}

export default function Profile({
  controller: c,
  model: m,
  selectedTab,
  passwordFieldsVisible,
}: Props) {
  const [, setIsDeleteAccountDialogOpen] =
    ConfirmDeleteAccountDialog.useIsOpen();

  const [, setIsUnableToDeleteAccountOpen] =
    UnableToDeleteAccountDialog.useIsOpen();

  const [, setIsUpdateEmailDialogOpen] = UpdateEmailDialog.useIsOpen();

  const isOwnerOfTeamQueries = useIsOwnerOfTeamWithMembers();

  useEffect(() => {
    new FeatureUseEventAdapter({
      feature: 'profile_page',
      action: 'open',
    }).sendAnalytics();
  }, []);

  if (isOwnerOfTeamQueries.queries.some((query) => query.isLoading)) {
    return (
      <div className="h-screen">
        <Spinner />
      </div>
    );
  }
  return (
    <>
      <div id="profile-container" className="max-w-7xl m-auto">
        <ProfileTabs
          bannerMessage={m?.banner?.message}
          bannerType={m?.banner?.type}
          closeBannerClicked={() => c.closeBannerClicked()}
          selectedTab={selectedTab}
        />
        <div id="profile-form-container" className="form pl-4 pr-4 md:pr-0">
          <div className="profile-form-grid">
            <Input
              id="firstName"
              label={translate('First Name')}
              maxLength={255}
              status={m.errors.firstName}
              value={m.firstName}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                c.firstNameChanged(event.target.value)
              }
            />
            <Input
              id="lastName"
              label={translate('Last Name')}
              maxLength={255}
              status={m.errors.lastName}
              value={m.lastName}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                c.lastNameChanged(event.target.value)
              }
            />
            <div className="email-container">
              <div className="label">
                <span>{translate('Email')}</span>
              </div>

              <p className="value">{m.email}</p>
              <div>
                <a
                  className="link profile-change-email-link"
                  href="#profile/info"
                  onClick={() => setIsUpdateEmailDialogOpen(true)}
                >
                  {translate('Change Email')}
                </a>
              </div>
              <div>
                {m.showChangePassword ? (
                  <a
                    className="link profile-change-password-link"
                    href="#profile/info"
                    onClick={c.changePasswordClicked}
                  >
                    {translate('Change Password')}
                  </a>
                ) : (
                  <>&nbsp;</>
                )}
              </div>
            </div>

            <span className="input-container">
              <div className="legacy-phone flex flex-row items-center">
                <label>{translate('Phone Number (optional)')}</label>
                <HelpTooltip
                  above={true}
                  title={null}
                  text={translate(
                    'This information is used by the Socrative team for purposes of contacting you about your account or learning opportunities.'
                  )}
                ></HelpTooltip>
              </div>
              <PhoneNumberInput
                phoneNumber={m.phoneNumber}
                showError={false}
                handleChange={(data) => c.phoneNumberChanged(data)}
              />
            </span>

            {passwordFieldsVisible ? (
              <>
                <Input
                  autoComplete="off"
                  containerClass="old-password-input"
                  id="currentPassword"
                  label={translate('Current Password')}
                  maxLength={255}
                  status={m.errors.currentPassword}
                  type={m.showPasswords ? 'text' : 'password'}
                  value={m.currentPassword}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    c.currentPasswordChanged(event.target.value)
                  }
                />
                <div></div>
                <Input
                  id="newPassword"
                  label={translate('New Password')}
                  maxLength={255}
                  status={m.errors.password}
                  type={m.showPasswords ? 'text' : 'password'}
                  value={m.password}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    c.newPasswordChanged(event.target.value)
                  }
                />
                <Input
                  id="newPassword2"
                  label={translate('Confirm New Password')}
                  maxLength={255}
                  status={m.errors.password2}
                  type={m.showPasswords ? 'text' : 'password'}
                  value={m.password2}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    c.newPassword2Changed(event.target.value)
                  }
                />
                <div id="show-passwords-container" className="">
                  <input
                    id="show-passwords-checkbox"
                    checked={m.showPasswords}
                    onChange={c.showPasswordsChanged}
                    type="checkbox"
                  />
                  <label
                    className="show-passwords-label"
                    htmlFor="show-passwords-checkbox"
                  >
                    {translate('Show Passwords')}
                  </label>
                </div>
              </>
            ) : null}
          </div>
          <div className="profile-button-container">
            <button
              id="profile-save-button"
              className="action-button action-button-200"
              onClick={c.saveClicked}
            >
              {translate('Save')}
            </button>
          </div>
          <div className="delete-account-container">
            <button
              className={cx('action-button action-button-200', {
                disabled: m.deletingAccount,
              })}
              aria-disabled={m.deletingAccount}
              id="delete-account-button"
              onClick={() => {
                if (isOwnerOfTeamQueries.cannotDelete) {
                  setIsUnableToDeleteAccountOpen(true);
                } else {
                  setIsDeleteAccountDialogOpen(true);
                }
              }}
              style={{
                color: '#CE0000',
                borderColor: '#CE0000',
                background: 'none',
              }}
            >
              {translate('Delete Account', true)}
            </button>
          </div>
        </div>
      </div>
      <ConfirmDeleteAccountDialog />
      <UnableToDeleteAccountDialog />
      <UpdateEmailDialog />
    </>
  );
}
